import type React from 'react';
import type { B2BInvoice } from '@hospasense/contracts';
import { Button, Modal, Input, Select, SearchableSelect } from '../components/ui/index.js';

interface CreateInvoiceModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  organisations: Array<{ id: string; displayName: string }>;
  contracts: Array<{ id: string; contractNumber: string }>;
  selectedOrgId: string;
  setSelectedOrgId: (val: string) => void;
  selectedContractId: string;
  setSelectedContractId: (val: string) => void;
  itemDescription: string;
  setItemDescription: (val: string) => void;
  subtotalInr: number;
  setSubtotalInr: (val: number) => void;
  placeOfSupply: string;
  setPlaceOfSupply: (val: string) => void;
  gstin: string;
  setGstin: (val: string) => void;
  recipientEmail: string;
  setRecipientEmail: (val: string) => void;
}

export function CreateInvoiceModal({
  open,
  onClose,
  onSubmit,
  organisations,
  contracts,
  selectedOrgId,
  setSelectedOrgId,
  selectedContractId,
  setSelectedContractId,
  itemDescription,
  setItemDescription,
  subtotalInr,
  setSubtotalInr,
  placeOfSupply,
  setPlaceOfSupply,
  gstin,
  setGstin,
  recipientEmail,
  setRecipientEmail,
}: CreateInvoiceModalProps) {
  if (!open) return null;
  return (
    <Modal open={open} title="Issue B2B Tax Invoice" onClose={onClose}>
      <form onSubmit={onSubmit} className="space-y-3">
        <SearchableSelect
          label="Organisation"
          required
          placeholder="Search and select client organisation…"
          value={selectedOrgId}
          onChange={(val) => setSelectedOrgId(val)}
          options={organisations.map((org) => ({
            value: org.id,
            label: org.displayName,
          }))}
        />

        <Select
          label="Contract"
          value={selectedContractId}
          onChange={(e) => setSelectedContractId(e.target.value)}
          required
        >
          {contracts.map((c) => (
            <option key={c.id} value={c.id}>
              {c.contractNumber}
            </option>
          ))}
        </Select>

        <Input
          label="Item Description"
          value={itemDescription}
          onChange={(e) => setItemDescription(e.target.value)}
          required
        />

        <div className="grid grid-cols-2 gap-2">
          <Input
            label="Subtotal (INR)"
            type="number"
            value={String(subtotalInr)}
            onChange={(e) => setSubtotalInr(Number(e.target.value))}
            required
          />
          <Input
            label="Place of Supply (State)"
            value={placeOfSupply}
            onChange={(e) => setPlaceOfSupply(e.target.value.toUpperCase())}
            required
          />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Input
            label="Customer GSTIN"
            value={gstin}
            onChange={(e) => setGstin(e.target.value)}
            required
          />
          <Input
            label="Recipient Email"
            type="email"
            value={recipientEmail}
            onChange={(e) => setRecipientEmail(e.target.value)}
            required
          />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit">
            Generate Tax Invoice
          </Button>
        </div>
      </form>
    </Modal>
  );
}

interface SettleInvoiceModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  paymentRef: string;
  setPaymentRef: (val: string) => void;
}

export function SettleInvoiceModal({
  open,
  onClose,
  onSubmit,
  paymentRef,
  setPaymentRef,
}: SettleInvoiceModalProps) {
  if (!open) return null;
  return (
    <Modal open={open} title="Record Invoice Settlement" onClose={onClose}>
      <form onSubmit={onSubmit} className="space-y-3">
        <Input
          label="Payment Reference (NEFT/RTGS)"
          value={paymentRef}
          onChange={(e) => setPaymentRef(e.target.value)}
          required
        />
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit">
            Confirm Settlement
          </Button>
        </div>
      </form>
    </Modal>
  );
}

interface VoidInvoiceModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  selectedInvoice: B2BInvoice;
  voidReason: string;
  setVoidReason: (val: string) => void;
}

export function VoidInvoiceModal({
  open,
  onClose,
  onSubmit,
  selectedInvoice,
  voidReason,
  setVoidReason,
}: VoidInvoiceModalProps) {
  if (!open) return null;
  return (
    <Modal open={open} title="Void Statutory Tax Invoice" onClose={onClose}>
      <form onSubmit={onSubmit} className="space-y-3">
        <p className="text-xs text-muted-foreground">
          This action writes a permanent audit record and voids tax invoice{' '}
          {selectedInvoice.invoiceNumber}.
        </p>
        <Input
          label="Reason for Cancellation"
          value={voidReason}
          onChange={(e) => setVoidReason(e.target.value)}
          required
        />
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="danger" type="submit">
            Confirm Void
          </Button>
        </div>
      </form>
    </Modal>
  );
}
